import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

export interface ConfirmConfig {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
}

interface ConfirmState extends ConfirmConfig {
  open: boolean;
}

/**
 * Estado del cuadro fuera de React: lo que cambia (abierto, «procesando») se lee con
 * `useSyncExternalStore` DENTRO de `ConfirmDialog`, así el componente que devuelve el hook
 * es SIEMPRE el mismo y React no lo desmonta ni lo vuelve a montar mientras está abierto
 * (un componente nuevo en cada render movía el foco a mitad de una acción).
 */
interface Instantanea {
  state: ConfirmState | null;
  running: boolean;
}

function crearCuadro() {
  let actual: Instantanea = { state: null, running: false };
  /** El elemento que tenía el foco al pedir la confirmación: ahí vuelve al cerrar. */
  let quienPregunto: HTMLElement | null = null;
  const oyentes = new Set<() => void>();
  const poner = (siguiente: Instantanea) => {
    actual = siguiente;
    oyentes.forEach((o) => o());
  };
  const suscribir = (o: () => void) => {
    oyentes.add(o);
    return () => {
      oyentes.delete(o);
    };
  };
  const leer = () => actual;

  /** Quien espera la respuesta de `preguntar`: se le contesta «no» si se cierra sin aceptar. */
  let pendiente: ((si: boolean) => void) | null = null;
  /** Cuántos cuadros dibujados hay: sin ninguno, `preguntar` no tendría quién lo muestre. */
  let montados = 0;

  const confirm = (config: ConfirmConfig) => {
    quienPregunto = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    poner({ state: { ...config, open: true }, running: false });
  };

  /**
   * Lo mismo que `confirm`, pero devuelve la respuesta: `true` si aceptó. Sirve para
   * reemplazar el `confirm()` del navegador sin rearmar la función que lo usaba:
   * `if (!(await preguntar({…}))) return;`.
   */
  const preguntar = (config: Omit<ConfirmConfig, 'onConfirm'>): Promise<boolean> => {
    /*
      Sin el cuadro dibujado no hay a quién preguntarle: se contesta «no», como si
      cancelara. En la app no pasa (el cuadro vive en el marco); en una prueba que
      dibuja la pantalla suelta, la acción simplemente no ocurre. No se usa el
      `confirm()` del navegador: el guardián `sin-dialogos-nativos` no lo deja.
    */
    if (montados === 0) return Promise.resolve(false);
    pendiente?.(false);
    return new Promise<boolean>((resolver) => {
      pendiente = resolver;
      confirm({
        ...config,
        onConfirm: () => {
          const r = pendiente;
          pendiente = null;
          r?.(true);
        },
      });
    });
  };

  // Igual que siempre: apretar la acción cierra el cuadro y el trabajo sigue por detrás.
  const cerrar = () => {
    const r = pendiente;
    pendiente = null;
    r?.(false);
    poner({ state: null, running: false });
  };

  const aceptar = async () => {
    const { state } = actual;
    if (!state) return;
    try {
      poner({ state, running: true });
      await state.onConfirm();
    } finally {
      poner({ state: null, running: false });
    }
  };

  function ConfirmDialog() {
    const { state, running } = useSyncExternalStore(suscribir, leer, leer);
    useEffect(() => {
      montados += 1;
      return () => {
        montados -= 1;
      };
    }, []);
    if (!state) return null;
    const isDestructive = state.variant === 'destructive';
    return (
      <AlertDialog
        open={state.open}
        onOpenChange={(open) => {
          if (!open) cerrar();
        }}
      >
        <AlertDialogContent
          // Radix devuelve el foco al «trigger»; acá no hay (se abre desde código): vuelve a
          // quien lo pidió, si sigue en pantalla. Si no, no se lo roba a nadie.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (quienPregunto?.isConnected) quienPregunto.focus({ preventScroll: true });
          }}
        >
          <AlertDialogHeader>
            {/* Un nombre larguísimo sin espacios (una regla, una estrategia) se
                corta en vez de desbordar la ventana (spec 294). */}
            <AlertDialogTitle className="[overflow-wrap:anywhere]">{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription className="[overflow-wrap:anywhere]">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={running}>
              {state.cancelText ?? 'Cancelar'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={aceptar}
              disabled={running}
              className={
                isDestructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {running ? 'Procesando...' : (state.confirmText ?? 'Aceptar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return { confirm, preguntar, ConfirmDialog };
}

/**
 * Hook que devuelve una función `confirm(config)` y un `<ConfirmDialog />` para renderizar.
 * Reemplaza window.confirm() con un AlertDialog tipado y consistente.
 *
 * Uso:
 * const { confirm, ConfirmDialog } = useConfirmDialog();
 * confirm({ title: 'Eliminar', description: '...', variant: 'destructive', onConfirm: async () => {...} });
 * <ConfirmDialog />  // renderizar al final del JSX
 *
 * `confirm` y `ConfirmDialog` tienen identidad estable durante toda la vida de quien usa el hook.
 */
export function useConfirmDialog() {
  const cuadro = useRef<ReturnType<typeof crearCuadro> | null>(null);
  if (!cuadro.current) cuadro.current = crearCuadro();
  return cuadro.current;
}

/*
  Un cuadro para toda la app, dibujado una vez en el marco (`MarcoDeLaApp`):
  `await confirmar({...})` reemplaza al `confirm()` del navegador —la ventanita
  gris— sin tener que dibujar un cuadro en cada pantalla.
*/
const cuadroGlobal = crearCuadro();

/** El cuadro de confirmación de toda la app. Va una sola vez, en el marco. */
export const DialogoDeConfirmacionGlobal = cuadroGlobal.ConfirmDialog;

/**
 * Pregunta con el diálogo de la app y devuelve la respuesta (`true` = aceptó).
 *
 * Uso: `if (!(await confirmar({ title: '¿Eliminar este contrato?', variant: 'destructive' }))) return;`
 */
export function confirmar(config: Omit<ConfirmConfig, 'onConfirm'>): Promise<boolean> {
  return cuadroGlobal.preguntar(config);
}
